/**
 * Linha do tempo dos próximos 90 dias de uma aeronave.
 *
 * Fontes (tudo já calculado no servidor):
 *   - aircraft.operational_state.criteria.documents  (as 5 validades)
 *   - insurance                                      (apólice atual)
 *   - items                                          (TODAS as tarefas de manutenção, com `status`)
 *   - perDayMin                                      (média de minutos de motor por dia, últimas 4 semanas)
 *   - covered                                        (nomes das tarefas por horas que outra maior já cobre: ficam de fora)
 *
 * Os prazos por calendário são datas; os prazos por horas só têm data ESTIMADA (horas em falta ÷ uso recente),
 * e por isso aparecem à parte e marcados como estimativa. Quem já passou o prazo (dentro da tolerância ou da
 * extensão do motor) fica preso ao "hoje", com uma barra até ao fim da tolerância quando se consegue saber.
 *
 * Cada entrada: { id, lane, title, days, overdue, severity, estimated, date, barDays, barKind, detail, target }
 *   lane      "docs" | "cal" | "hours"
 *   severity  "critical" | "warn" | "info" | "ok"   ("ok" = ainda longe: azul acinzentado)
 */

export const HORIZON_DAYS = 90;

export function daysUntil(iso, now = new Date()) {
  if (!iso) return null;
  const d = new Date(iso + "T00:00:00");
  const t = new Date(now);
  t.setHours(0, 0, 0, 0);
  return Math.round((d - t) / 86400000);
}

export function isoInDays(days, now = new Date()) {
  const d = new Date(now);
  d.setHours(12, 0, 0, 0);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export function fmtDM(iso) {
  return iso && iso.length >= 10 ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}` : "";
}

const fmtHM = (min) => {
  const v = Math.abs(min || 0);
  return `${Math.floor(v / 60)}:${String(v % 60).padStart(2, "0")}`;
};

/** Dias até as horas em falta se gastarem, ao ritmo recente (null se não há ritmo para estimar). */
export function estimateDays(minutesLeft, perDayMin) {
  if (minutesLeft == null || !(perDayMin > 0)) return null;
  return Math.max(0, Math.ceil(minutesLeft / perDayMin));
}

// Mesmos limiares dos alertas: informação até 45 dias, atenção até 30
const sevByDays = (days) => (days <= 30 ? "warn" : days <= 45 ? "info" : "ok");

export function buildTimeline({ aircraft, items, insurance, perDayMin, covered, now = new Date() }) {
  const coveredSet = new Set(covered || []);
  const entries = [];
  let beyond = 0;       // prazos conhecidos depois dos 90 dias
  let noEstimate = 0;   // prazos por horas sem ritmo de voo para estimar a data

  const push = (e) => entries.push({ estimated: false, overdue: false, barDays: null, barKind: null, date: null, ...e });

  // Documentos
  const docs = aircraft?.operational_state?.criteria?.documents || {};
  Object.entries(docs).forEach(([key, d]) => {
    if (!d || d.missing || d.days_remaining == null) return;
    const title = d.label || key;
    if (d.days_remaining < 0) {
      push({ id: `doc:${key}`, lane: "docs", title, days: 0, overdue: true, severity: "critical", detail: `${title}: expirou há ${-d.days_remaining} dias (${d.valid_until})`, target: "documents" });
    } else if (d.days_remaining <= HORIZON_DAYS) {
      push({ id: `doc:${key}`, lane: "docs", title, days: d.days_remaining, severity: sevByDays(d.days_remaining), date: d.valid_until, detail: `${title}: expira a ${d.valid_until}`, target: "documents" });
    } else beyond += 1;
  });

  // Seguro
  if (insurance?.policy_end) {
    const days = daysUntil(insurance.policy_end, now);
    const title = insurance.insurer ? `Seguro (${insurance.insurer})` : "Seguro";
    if (days < 0) {
      push({ id: "insurance", lane: "docs", title, days: 0, overdue: true, severity: "critical", detail: `${title}: expirou há ${-days} dias (${insurance.policy_end})`, target: "insurance" });
    } else if (days <= HORIZON_DAYS) {
      push({ id: "insurance", lane: "docs", title, days, severity: sevByDays(days), date: insurance.policy_end, detail: `${title}: termina a ${insurance.policy_end}`, target: "insurance" });
    } else beyond += 1;
  }

  // Manutenções (todas as tarefas ativas)
  (items || []).filter((it) => !it.archived).forEach((it) => {
    const st = it.status || {};
    const title = `${it.component || "Componente"} · ${it.task || "manutenção"}`;
    const engineMin = it.current_hours_min;

    if (st.rule === "potential") {
      const rb = st.replace_by || {};
      const dDate = rb.date != null ? rb.days_left : null;
      const dHours = rb.hours_min != null ? estimateDays(rb.minutes_left, perDayMin) : null;
      if (st.state === "out_of_tolerance") {
        push({ id: `m:${it.id}`, lane: "cal", title, days: 0, overdue: true, severity: "critical", detail: `${title}: ${st.reason || "potencial esgotado"}`, target: "maintenance" });
        return;
      }
      const options = [dDate, dHours].filter((x) => x != null);
      if (!options.length) { if (rb.hours_min != null && dHours == null) noEstimate += 1; return; }
      const chosen = Math.max(0, Math.min(...options));
      const fromHours = dHours != null && chosen === dHours && (dDate == null || dHours < dDate);
      const inExtension = st.state === "in_tolerance";
      const limit = [rb.date ? `${rb.date}` : null, rb.hours_min != null ? `${fmtHM(rb.hours_min)} de motor` : null].filter(Boolean).join(" ou ");
      if (chosen > HORIZON_DAYS) { beyond += 1; return; }
      push({
        id: `m:${it.id}`, lane: fromHours ? "hours" : "cal", title, days: inExtension ? 0 : chosen, overdue: inExtension,
        estimated: fromHours, date: inExtension ? null : isoInDays(chosen, now),
        severity: inExtension ? "warn" : sevByDays(chosen),
        barDays: inExtension ? chosen : null, barKind: inExtension ? "extension" : null,
        detail: `${title}: ${inExtension ? "em extensão do potencial; substituir até " : "substituir até "}${limit}`, target: "maintenance",
      });
      return;
    }

    // Prazo por calendário
    if (st.next_due_date) {
      const days = st.days_remaining != null ? st.days_remaining : daysUntil(st.next_due_date, now);
      if (st.calendar_state === "out_of_tolerance") {
        push({ id: `m:${it.id}:cal`, lane: "cal", title, days: 0, overdue: true, severity: "critical", detail: `${title}: calendário fora de tolerância (devia ter sido a ${st.next_due_date})`, target: "maintenance" });
      } else if (st.calendar_state === "in_tolerance") {
        const left = st.limit_date ? Math.max(0, daysUntil(st.limit_date, now)) : null;
        push({ id: `m:${it.id}:cal`, lane: "cal", title, days: 0, overdue: true, severity: "warn", barDays: left, barKind: "tolerance", detail: `${title}: vencida a ${st.next_due_date}, tolerância até ${st.limit_date}`, target: "maintenance" });
      } else if (days <= HORIZON_DAYS) {
        push({ id: `m:${it.id}:cal`, lane: "cal", title, days: Math.max(0, days), severity: st.calendar_severity === "critical" || st.calendar_severity === "warn" ? "warn" : st.calendar_severity || sevByDays(days), date: st.next_due_date, detail: `${title}: ${st.next_due_date}`, target: "maintenance" });
      } else beyond += 1;
    }

    // Prazo por horas: data estimada (a que outra tarefa maior já cobre não se desenha: o calendário dela, se tiver, sim)
    if (st.minutes_remaining != null && !coveredSet.has(title)) {
      if (st.hours_state === "out_of_tolerance") {
        push({ id: `m:${it.id}:hrs`, lane: "hours", title, days: 0, overdue: true, severity: "critical", detail: `${title}: horas fora de tolerância (${st.reason || ""})`, target: "maintenance" });
      } else if (st.hours_state === "in_tolerance") {
        const minsToLimit = st.limit_hours_min != null && engineMin != null ? st.limit_hours_min - engineMin : null;
        const left = estimateDays(minsToLimit, perDayMin);
        push({ id: `m:${it.id}:hrs`, lane: "hours", title, days: 0, overdue: true, severity: "warn", estimated: true, barDays: left, barKind: "tolerance", detail: `${title}: vencida há ${fmtHM(st.minutes_remaining)}, tolerância até +${fmtHM(st.tolerance_hours_min)}` + (minsToLimit != null ? ` (faltam ${fmtHM(minsToLimit)} de motor)` : ""), target: "maintenance" });
      } else {
        const est = estimateDays(st.minutes_remaining, perDayMin);
        if (est == null) noEstimate += 1;
        else if (est <= HORIZON_DAYS) {
          push({ id: `m:${it.id}:hrs`, lane: "hours", title, days: est, estimated: true, severity: st.hours_severity === "critical" || st.hours_severity === "warn" ? "warn" : st.hours_severity || sevByDays(est), date: isoInDays(est, now), detail: `${title}: faltam ${fmtHM(st.minutes_remaining)} de motor (data estimada)`, target: "maintenance" });
        } else beyond += 1;
      }
    }
  });

  entries.sort((a, b) => a.days - b.days || a.title.localeCompare(b.title));
  return { entries, beyond, noEstimate, perDayMin: perDayMin || 0 };
}
